import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useMemo, useState } from "react";
import classNames from "classnames";

// context
import { ContractContext } from "../../../providers/contract";
import { LOCALIZE_DATE, ROUTES } from "../../../setup/constants";
import { ECONTRACT_STATUS } from "../../../enums";
import {
  EContractType,
  ETrasactionsType,
} from "../../../setup/api/services/contract/models";
import { getUnregisteredContractParties } from "../../../setup/util/partyRegistration";
import { options as dateOptions } from "../../../components/contractListItem";
import { NUMBER, PARENT_NUMBER } from "../constants";

// components
import PrimaryButton from "../../../components/common/PrimaryButton";
import PDFComponent from "../../../components/common/PDFComponent";
import ContractActions from "../actions";
import ContractAction from "../ContractActions";
import CashoutCard from "../../cashouts/CashoutCard";
import AdditionalInformation from "../contractDetails/addittionalDetails";
import LeftSection from "../authenticatedContractDetails/LeftSection";
import OperationMetadata from "../authenticatedContractDetails/OperationMetadata";
import EscalationStatusBanner from "../authenticatedContractDetails/EscalationStatusBanner";
import SettlementModal from "../authenticatedContractDetails/SettlementModal";
import SettlementAdvisory from "../authenticatedContractDetails/SettlementAdvisory";
import SuggestedSettlement from "../authenticatedContractDetails/SuggestedSettlement";
import UnregisteredPartiesAlert from "../authenticatedContractDetails/UnregisteredPartiesAlert";
import BuyRequestInfo from "../authenticatedContractDetails/BuyRequestInfo";
import ShippingContactsInfo from "../authenticatedContractDetails/ShippingContactsInfo";
import ShippingDetails from "../authenticatedContractDetails/ShippingDetails";
import StatsDetails from "./StatsDetails";
import Status from "./Status";
import MainDetails from "./MainDetails";
import CashoutDetails from "./CashoutDetails";
import BankTransferProof from "./BankTransferProof";

/**
 * Details page of a milestone contract. The top of the page mirrors the
 * business portal's milestone details page; below it sit the components of the
 * default contract details page — payment breakdown, parties, settlement and
 * cash-out — unchanged, so every admin flow works as it did there.
 */
const MilestoneDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contract: milestone, actions } = useContext(ContractContext);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openSettlementModal, setOpenSettlementModal] = useState(false);

  const canAdminSettleContract =
    milestone?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER ||
    milestone?.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER ||
    milestone?.status === ECONTRACT_STATUS.INSPECTION_ESCALATION ||
    milestone?.status === ECONTRACT_STATUS.DELIVERY_ESCALATION ||
    milestone?.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_CLOSE ||
    milestone?.status === ECONTRACT_STATUS.WAITING_FOR_SETTLEMENT_RESOLVE ||
    milestone?.status === ECONTRACT_STATUS.ITEM_ACCEPTED ||
    milestone?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT;

  // no settlement can be sent for a party the backend cannot resolve to a real
  // user, so the action stays blocked until every party is registered
  // the portal's tabs: the parties as agreed, and the transfers made to them
  const list = useMemo(() => {
    return [
      {
        id: 0,
        title: t("lbl_main_tab_title"),
      },
      {
        id: 1,
        title: t("lbl_transfer_tab_title"),
      },
    ];
  }, [t]);

  const unregisteredParties = useMemo(
    () => getUnregisteredContractParties(milestone),
    [milestone]
  );

  const viewCashoutCards =
    milestone?.status === ECONTRACT_STATUS.CASH_OUT_APPROVED ||
    milestone?.status === ECONTRACT_STATUS.PARTIAL_CASHOUT;

  const refreshContractDetails = useCallback(() => {
    if (
      milestone?.transactions?.some(
        (transaction) =>
          transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
          (transaction?.paymentMethod === "TABBY" ||
            transaction?.paymentMethod === "NEO" ||
            transaction?.paymentMethod === "TAMARA")
      )
    )
      setTimeout(() => actions?.getContract(), 1000);
    else actions?.getContract();
  }, [actions, milestone?.transactions]);

  if (!milestone) return <></>;

  return (
    <div className={styles.outerContainer}>
      <OperationMetadata />
      <div className={styles.contractContainer}>
        <div className={styles.headerContainer}>
          <div className={styles.headerBlock}>
            <h1 className={styles.title}>{milestone?.itemDetail?.title}</h1>
            <p className={styles.subtitle}>{`${t("lbl_deadline")} : ${
              milestone?.deadLine
                ? new Date(milestone?.deadLine).toLocaleDateString("ar-EG", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : ""
            }`}</p>
            <p className={styles.subtitle}>
              {`${NUMBER}: ${milestone?.shortId}# · ${new Date(
                milestone.createdAt
              ).toLocaleDateString(LOCALIZE_DATE, dateOptions)}`}
              {milestone?.parentId && (
                <>
                  {` · ${PARENT_NUMBER} `}
                  <span
                    className={styles.link}
                    onClick={() => {
                      navigate(`${ROUTES.CONTRACT}${milestone?.parentId}`);
                      actions?.resetContractState();
                    }}>
                    {milestone?.parentId}#
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        {canAdminSettleContract && (
          <UnregisteredPartiesAlert parties={unregisteredParties} />
        )}

        <Status
          contract={milestone}
          actions={
            <>
              {canAdminSettleContract && (
                <PrimaryButton
                  style={{
                    width: "auto",
                    height: "40px",
                    padding: "0 16px",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    lineHeight: "100%",
                    fontWeight: 500,
                    background: unregisteredParties.length
                      ? "#9FA6AB"
                      : "#0D44AD",
                    cursor: unregisteredParties.length
                      ? "not-allowed"
                      : "pointer",
                  }}
                  disabled={!!unregisteredParties.length}
                  onClick={() => {
                    if (unregisteredParties.length) return;
                    setOpenSettlementModal(true);
                  }}>
                  {t("global_status_settlement")}
                </PrimaryButton>
              )}
              <ContractActions
                inline
                id="ContractActions"
                contract={milestone}
                feedBack={refreshContractDetails}
              />
            </>
          }
        />

        <div className={styles.fullWidth}>
          <EscalationStatusBanner />
        </div>

        {milestone?.settlementAdvisory && (
          <div className={styles.fullWidth}>
            <SettlementAdvisory />
          </div>
        )}

        {canAdminSettleContract &&
          !!milestone?.suggestedCashOutAmountList?.length && (
            <div className={styles.fullWidth}>
              <SuggestedSettlement compactButton />
            </div>
          )}

        <StatsDetails milestoneId={milestone?.id || ""} />

        {!!milestone?.cashOutAmountList && (
          <div className={styles.tabContainer}>
            {list?.map((listItem) => (
              <li
                onClick={() => setSelectedIndex(listItem?.id)}
                key={listItem?.title}
                className={classNames(
                  styles.listItem,
                  {
                    [styles.listItemSelected]: listItem?.id === selectedIndex,
                  },
                  { [styles.firstItem]: listItem?.id === 0 },
                  {
                    [styles.lastItem]:
                      listItem?.id === list?.[list?.length - 1]?.id,
                  }
                )}>
                {listItem?.title}
              </li>
            ))}
          </div>
        )}

        {selectedIndex === 0 && <MainDetails milestone={milestone} />}

        {selectedIndex === 1 && <CashoutDetails milestone={milestone} />}

        {/* The default contract details' components, kept as they were. */}
        <div className={styles.lowerSection}>
          <div className={styles.lowerMain}>
            <BankTransferProof milestone={milestone} />
            <div className={styles.panel}>
              <AdditionalInformation contract={milestone} />
            </div>
            {viewCashoutCards && (
              <div className={styles.cashoutCardsContainer}>
                <h4 className={styles.sectionTitle}>
                  {t("lbl_the_settlement")}
                </h4>
                {milestone?.cashOutAmountList?.map((cashOutAmount, index) => (
                  <CashoutCard
                    key={index}
                    cashOutAmount={cashOutAmount}
                    disableCashout={cashOutAmount?.multiCashOut}
                    hideFees={
                      milestone.type !== EContractType.QUICK_TRANSACTION
                    }
                  />
                ))}
              </div>
            )}
            <div className={styles.fullWidth}>
              <ContractAction />
            </div>
          </div>
          <div className={styles.lowerSide}>
            <BuyRequestInfo />
            <ShippingContactsInfo />
            <ShippingDetails />
            <div className={styles.panel}>
              <LeftSection
                hideStatus
                contract={milestone}
                refreshContractDetails={refreshContractDetails}
              />
            </div>
          </div>
        </div>

        {(!!milestone?.acceptanceConditions ||
          !!milestone?.cancellationConditions) && (
          <div className={styles.terms_conditions}>
            <h4>{t("lbl_terms_conditions")}</h4>
            <div
              className={classNames(
                styles.terms_conditions_wrapper,
                styles.card
              )}>
              {!!milestone?.acceptanceConditions && (
                <div className={styles.terms_conditions_content}>
                  <p className={styles.title}>
                    {t("lbl_acceptance_conditions")}
                  </p>
                  <p className={styles.description}>
                    {milestone?.acceptanceConditions}
                  </p>
                </div>
              )}
              {!!milestone?.cancellationConditions && (
                <div className={styles.terms_conditions_content}>
                  <p className={styles.title}>
                    {t("lbl_cancellation_conditions")}
                  </p>
                  <p className={styles.description}>
                    {milestone?.cancellationConditions}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.attachments}>
          {((milestone?.itemDetail?.attachments &&
            milestone?.itemDetail?.attachments?.length !== 0) ||
            (milestone?.itemDetail?.images &&
              milestone?.itemDetail?.images?.length !== 0)) && (
            <h2>{t("lbl_attachments")}</h2>
          )}

          <div className={styles.attachmentsFiles}>
            {milestone?.itemDetail?.attachments?.map((attachment) => (
              <PDFComponent
                key={attachment}
                isHorizontalPDF
                fileUrl={attachment}
              />
            ))}
          </div>

          <div className={styles.attachmentsImages}>
            {milestone?.itemDetail?.images?.map((image) => (
              <img key={image} src={image} alt="image" />
            ))}
          </div>
        </div>
      </div>
      <SettlementModal
        open={openSettlementModal}
        handleClose={() => setOpenSettlementModal(false)}
      />
    </div>
  );
};

export default MilestoneDetails;
